import React, { useState, useCallback } from 'react';
import moment from 'moment';
import { useLanguage } from '../locale';
import {
  useGetMainKPIs,
  useGetCurrentCycle,
  useGetMainKPIDetails,
  useRegisteredUsers,
} from '../hooks/api/company.api';
import { useFeedbackCategories } from '../hooks/useFeedbackCategories';
import useFeatureFlags from '../hooks/useFeatureFlags';
import { MainKPIItem } from '../models/analytics.models';
import GaugeChart from './GaugeChart';
import AreasSectionSkeleton from './AreasSectionSkeleton';
import AnonymousWarningBanner from './AnonymousWarningBanner';
import MainKPIDetailsPanel from './MainKPIDetailsPanel';
import MainKPIDetailsPanelSkeleton from './MainKPIDetailsPanelSkeleton';

interface AreasNewNewSectionProps {
  companyId: string;
  startDate: moment.Moment;
  endDate: moment.Moment;
  teamId?: string;
  userId?: string;
  containerClassName?: string;
}

interface ProcessedKPIItem extends MainKPIItem {
  gaugeIndicator: number;
  mappedSentiment: 'positive' | 'negative' | 'neutral';
}

const AreasNewNewSection: React.FC<AreasNewNewSectionProps> = ({
  companyId,
  startDate,
  endDate,
  teamId,
  userId,
  containerClassName = '',
}) => {
  const [t] = useLanguage();
  const feedbackCategories = useFeedbackCategories();
  const [featureFlags] = useFeatureFlags();
  const [selectedMainKPI, setSelectedMainKPI] = useState<string | null>(null);

  // Check if PulseSurvey feature is enabled
  const isPulseSurveyEnabled = featureFlags.PulseSurvey;

  // Get current cycle data - only if PulseSurvey is enabled
  const { data: currentCycle, isLoading: isLoadingCurrentCycle } = useGetCurrentCycle(companyId, {
    enabled: isPulseSurveyEnabled,
  });

  // Get registered users count
  const { data: registeredUsers, isLoading: isLoadingRegisteredUsers } = useRegisteredUsers(companyId, {
    from: startDate.format('YYYY-MM-DD'),
    to: endDate.format('YYYY-MM-DD'),
  }, teamId, userId);

  // Get main KPI details when a KPI is selected - only if PulseSurvey is enabled
  const { data: mainKPIDetails, isLoading: isLoadingMainKPIDetails } = useGetMainKPIDetails({
    companyId,
    cycleId: currentCycle?.cycleId || '',
    mainKpiId: selectedMainKPI || '',
    teamId,
    userId,
  }, {
    enabled: isPulseSurveyEnabled,
  });
  const { data, isLoading: isLoadingMainKPIs, error } = useGetMainKPIs({
    companyId: companyId!,
    from: startDate.format('YYYY-MM-DD'),
    to: endDate.format('YYYY-MM-DD'),
    teamId,
    userId,
  });

  // Combined loading state for all critical API calls needed for initial render
  const isPageLoading = React.useMemo(() => {
    // Always wait for main KPIs and registered users
    let loading = isLoadingMainKPIs || isLoadingRegisteredUsers;
    
    // If PulseSurvey is enabled, also wait for current cycle
    if (isPulseSurveyEnabled) {
      loading = loading || isLoadingCurrentCycle;
    }
    
    return loading;
  }, [isLoadingMainKPIs, isLoadingRegisteredUsers, isLoadingCurrentCycle, isPulseSurveyEnabled]);

  const processedData = React.useMemo(() => {
    if (!data?.items) return [];

    return data.items
      .map((item): ProcessedKPIItem => {
        return {
          ...item,
          gaugeIndicator: item.score || 0,
          mappedSentiment: item.categorySentiment,
        };
      })
      .sort((a, b) => b.gaugeIndicator - a.gaugeIndicator); // Sort by gauge indicator descending
  }, [data?.items]);

  // Function to get category title from feedback categories config
  const getCategoryTitle = (categoryId: string) => {
    const category = feedbackCategories.find((cat) => cat.id === categoryId);
    return category ? category.title : categoryId.replace('#', '');
  };

  // Handle KPI click - only if PulseSurvey is enabled
  const handleKPIClick = useCallback((categoryId: string) => {
    if (!isPulseSurveyEnabled) {
      return; // Don't do anything if PulseSurvey feature is disabled
    }

    if (selectedMainKPI === categoryId) {
      setSelectedMainKPI(null); // Close if already selected
    } else {
      setSelectedMainKPI(categoryId); // Open details

      // Scroll to panel immediately when skeleton appears
      setTimeout(() => {
        const element = document.getElementById('main-kpi-details-panel');
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
        }
      }, 50); // Very small delay to allow skeleton rendering
    }
  }, [isPulseSurveyEnabled, selectedMainKPI]);


  // Process main KPI details
  const selectedKPIData = React.useMemo(() => {
    if (!mainKPIDetails?.items || !selectedMainKPI) return null;

    // Get specific KPI titles from current cycle (moved inside useMemo)
    const getSpecificKPITitlesForMemo = (mainKpiId: string) => {
      if (!currentCycle?.surveySequence) return [];
      const survey = currentCycle.surveySequence.find(
        (s) => s.mainKPI === mainKpiId
      );
      return survey?.specificKPIs || [];
    };

    const mainKPI = mainKPIDetails.items.find(
      (item) => item.kpiType === 'main'
    );
    const specificKPIs = mainKPIDetails.items.filter(
      (item) => item.kpiType === 'specific'
    );
    const specificKPITitles = getSpecificKPITitlesForMemo(selectedMainKPI);

    // Find the parent KPI data from processedData
    const mainKPIParentData = processedData.find(
      (item) => item.category === selectedMainKPI
    );

    return {
      mainKPI,
      specificKPIs,
      specificKPITitles,
      mainKPIParentData,
    };
  }, [mainKPIDetails, selectedMainKPI, currentCycle, processedData]);

  if (isPageLoading) {
    return <AreasSectionSkeleton containerClassName={containerClassName} />;
  }

  if (error || !data?.items) {
    return (
      <div className="mt-8">
        <div className="text-center py-8 text-gray-500">
          {t('shared.noData')}
        </div>
      </div>
    );
  }

  // If data.items is an empty array, show AnonymousWarningBanner (user is anonymous)
  if (data.items.length === 0) {
    return (
      <div className="mt-8">
        <AnonymousWarningBanner />
      </div>
    );
  }

  return (
    <div className="mt-8">
      <div className={`mt-6 ${containerClassName}`}>
        <div className="grid grid-cols-4 gap-4">
          {processedData.map((item) => (
            <div key={item.category} className="flex flex-col items-center">
              <GaugeChart
                score={item.gaugeIndicator} // Show gauge indicator as score
                category={getCategoryTitle(item.category)}
                sentiment={item.mappedSentiment}
                className="flex-shrink-0"
                showIcon={true}
                categoryId={item.category}
                onClick={() => handleKPIClick(item.category)}
                clickable={isPulseSurveyEnabled}
              />
            </div>
          ))}
        </div>

        {/* Main KPI Details Panel or Skeleton - only if PulseSurvey is enabled */}
        {isPulseSurveyEnabled && selectedMainKPI && (
          <>
            {isLoadingMainKPIDetails ? (
              /* Show skeleton while loading */
              <MainKPIDetailsPanelSkeleton
                onClose={() => {
                  setSelectedMainKPI(null);
                }}
              />
            ) : selectedKPIData?.mainKPI && registeredUsers ? (
              /* Show actual panel with data */
              <MainKPIDetailsPanel
                mainKPI={selectedKPIData.mainKPI}
                mainKPIParentData={selectedKPIData.mainKPIParentData}
                specificKPIs={selectedKPIData.specificKPIs}
                specificKPITitles={selectedKPIData.specificKPITitles}
                registeredUsers={registeredUsers}
                onClose={() => {
                  setSelectedMainKPI(null);
                }}
              />
            ) : (
              /* Show panel with only header and 0% completion if no specific KPIs */
              <MainKPIDetailsPanel
                mainKPI={{
                  entityType: 'company',
                  entityId: companyId,
                  kpiType: 'main',
                  kpiId: selectedMainKPI,
                  mainkpiid: selectedMainKPI,
                  kpiScore: 0,
                  cyclestartdate: '',
                  completedCount: 0,
                  companyId: companyId,
                  cycleId: currentCycle?.cycleId || '',
                }}
                mainKPIParentData={processedData.find(
                  (item) => item.category === selectedMainKPI
                )}
                specificKPIs={[]}
                specificKPITitles={[]}
                registeredUsers={registeredUsers || 0}
                onClose={() => {
                  setSelectedMainKPI(null);
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AreasNewNewSection;
